import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Breadcrum from '../../components/common/Breadcrum';
import ServerTable from '../../components/table/ServerTable';
import Select from '../../components/form/Select';
import Models from '../../components/common/Models';
import moment from 'moment';
import axiosInstance from '../../services/axios';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

export default function AdminLeads() {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('ALL');
  const [assignedToFilter, setAssignedToFilter] = useState('ALL');
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  
  const [tableRow, setTableRow] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadsList, setLeadsList] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [sourcesList, setSourcesList] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [tableAction, setTableAction] = useState(null); 
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    source: '',
    assignedTo: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [totalLeads, setTotalLeads] = useState(0);

  const breadcrumbItems = [
    { label: 'Leads Management', path: '/admin/leads' }
  ];

  const tableColumns = [
    { id: 'serialNo', label: 'S.No', type: 'text' },
    { id: 'name', label: 'Name', type: 'text' },
    { id: 'email', label: 'Email', type: 'text' },
    { id: 'phone', label: 'Phone', type: 'text' },
    { id: 'source', label: 'Source', type: 'text' },
    { id: 'assignedTo', label: 'Assigned To', type: 'text' },
    { id: 'createdAt', label: 'Created', type: 'date' },
    {
      id: "action",
      label: "Action",
      type: "action",
      actions: [
        { id: "edit", name: "Edit", className: "w-[80px] bg-blue-500 text-white" },
        { id: "delete", name: "Delete", className: "w-[80px] bg-red-500 text-white" }
      ]
    }
  ];

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  const getEmployeeList = async () => {
    try {
      const response = await axiosInstance.get('/admin/employee/list');
      setEmployeeList(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch employee list');
    }
  };

  const getLeadSources = async () => {
    try {
      const response = await axiosInstance.get('/leads/sources');
      setSourcesList(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch lead sources');
    }
  };

  const getLeadsList = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: rowsPerPage,
        search: searchTerm || undefined,
        source: sourceFilter === 'ALL' ? undefined : sourceFilter,
        assignedTo: assignedToFilter === 'ALL' ? undefined : assignedToFilter,
        startDate: startDate ? moment(startDate).format('YYYY-MM-DD') : undefined,
        endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : undefined
      };

      const response = await axiosInstance.get('/leads', { params });
      setLeadsList(response.data.data.leads || []);
      setTotalLeads(response.data.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch leads list');
      setLeadsList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTableAction = async (action, row) => {
    if (action === 'View') {
      // View lead details
      setTableAction({ type: 'view', lead: row });
      console.log('View lead:', row.uuid);
    } else if (action === 'Edit') {
      setTableAction({ type: 'edit', lead: row });
      setSelectedLead(row);
      setFormData({
        name: row.originalName || '',
        email: row.originalEmail || '',
        phone: row.originalPhone || '',
        source: row.originalSource || '',
        assignedTo: row.assignedToId || ''
      });
      setIsModalOpen(true);
    } else if (action === 'Assign') {
      setTableAction({ type: 'assign', lead: row });
      // Handle lead assignment
      setSelectedLead(row);
      setFormData({
        ...formData,
        assignedTo: row.assignedToId || ''
      });
       setIsModalOpen(true);
      // Open assignment modal or handle directly
      
    } else if (action === 'Delete') {
      setTableAction({ type: 'delete', lead: row });
      if (window.confirm('Are you sure you want to delete this lead?')) {
        try {
          setLoading(true);
          await axiosInstance.delete(`/leads/${row.uuid}`);
          toast.success('Lead deleted successfully');
          getLeadsList();
        } catch (error) {
          toast.error('Failed to delete lead');
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (selectedLead) {
        await axiosInstance.put(`/leads/${selectedLead.uuid}`, formData);
        toast.success('Lead updated successfully');
      } else {
        await axiosInstance.post('/leads', formData);
        toast.success('Lead created successfully');
      }
      getLeadsList();
      resetForm();
    } catch (error) {
      toast.error(selectedLead ? 'Failed to update lead' : 'Failed to create lead');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    setLoading(true);
    try {
      await axiosInstance.post('/leads/upload-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      toast.success('Leads uploaded successfully');
      getLeadsList();
      resetUploadForm();
    } catch (error) {
      toast.error('Failed to upload leads');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      source: '',
      assignedTo: ''
    });
    setSelectedLead(null);
    setIsModalOpen(false);
  };

  const resetUploadForm = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setIsUploadModalOpen(false);
  };

  // Generate filter options
  const sourceOptions = [
    { value: "ALL", label: "All Sources" },
    ...sourcesList.map(source => ({ value: source, label: source }))
  ];

  const employeeOptions = [
    { value: "ALL", label: "All Employees" },
    ...employeeList.map(emp => ({ value: emp.id, label: emp.name }))
  ];

  useEffect(() => {
    getEmployeeList();
    getLeadSources();
  }, []);

  useEffect(() => {
    getLeadsList();
  }, [currentPage, rowsPerPage, searchTerm, sourceFilter, assignedToFilter, startDate, endDate]);

  useEffect(() => {
    if (leadsList && Array.isArray(leadsList)) {
      const updatedRows = leadsList.map((lead, index) => ({
        uuid: lead.id,
        serialNo: (currentPage - 1) * rowsPerPage + index + 1,
        name: lead.name || 'N/A',
        email: lead.email || 'N/A',
        phone: lead.phone || 'N/A',
        source: lead.source || 'N/A',
        assignedTo: lead.employee?.name || 'Unassigned',
        assignedToId: lead.employee?.id,
        createdAt: moment(lead.createdAt).format('YYYY-MM-DD'),
        originalName: lead.name,
        originalEmail: lead.email,
        originalPhone: lead.phone,
        originalSource: lead.source
      }));
      setTableRow(updatedRows);
    } else {
      setTableRow([]);
    }
  }, [leadsList, currentPage, rowsPerPage]);

  return (
    <>
      <div className='bg-gray-200 dark:bg-gray-800 py-2 px-4 rounded-lg flex justify-between items-center flex-col md:flex-row'>
        <div>
          <h1 className='text-2xl font-semibold text-theme-dark dark:text-theme-light'>Leads Management</h1>
          <Breadcrum items={breadcrumbItems} />
        </div>
        <div className='flex py-2 justify-end items-center gap-3 flex-wrap md:flex-nowrap '>
            <Select
            options={employeeOptions}
            value={assignedToFilter}
            onChange={(selected) => setAssignedToFilter(selected)}
            placeholder="Filter by Employee"
            className="w-auto md:w-48"
          />
          <DatePicker
            className='p-2 w-[100%] md:w-48 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100'
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => setDateRange(update)}
            isClearable={true}
            placeholderText="Select date range"
          />
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg whitespace-nowrap"
          >
            Upload Excel
          </button>
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg whitespace-nowrap"
          >
            Add New Lead
          </button>
        </div>
      </div>
        
      <div className='mt-4'>
        <ServerTable
          tableColumns={tableColumns}
          title="Leads"
          data={tableRow}
          total={totalLeads}
          loading={loading}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          handleTableAction={handleTableAction}
        />
      </div>

      {/* Lead Form Modal */}
      <Models isOpen={isModalOpen} isClose={resetForm}>
        <div className="p-6 max-w-2xl w-full bg-white dark:bg-gray-800">
          <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">
            {selectedLead ? 'Edit Lead' : 'Add New Lead'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Enter lead name"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Enter email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Phone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Enter phone number"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Source</label>
                  <input
                    type="text"
                    value={formData.source}
                    onChange={(e) => setFormData({...formData, source: e.target.value})}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Enter lead source"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Assign To</label>
                  <select
                    value={formData.assignedTo}
                    onChange={(e) => setFormData({...formData, assignedTo: e.target.value})}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">Select Employee</option>
                    {employeeList.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.name} ({emp.email})</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Saving...' : (selectedLead ? 'Update Lead' : 'Create Lead')}
              </button>
            </div>
          </form>
        </div>
      </Models>

      {/* Upload Excel Modal */}
      <Models isOpen={isUploadModalOpen} isClose={resetUploadForm}>
        <div className="p-6 max-w-md w-full bg-white dark:bg-gray-800">
          <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Upload Leads Excel</h2>
          <form onSubmit={handleFileUpload}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Select Excel File (.xls, .xlsx)
              </label>
              <input
                type="file"
                accept=".xls,.xlsx"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                required
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Supported columns: Name, Email, Phone, Source, Assigned To, Assigned By
              </p>
            </div>

            {uploadProgress > 0 && (
              <div className="mb-4">
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                    style={{width: `${uploadProgress}%`}}
                  ></div>
                </div>
                <p className="text-sm text-center mt-1">{uploadProgress}% uploaded</p>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={resetUploadForm}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </form>
        </div>
      </Models>
    </>
  );
}
